import { actions } from '..';
import * as api from '../../api';
import { saveApplicationSettings } from '../../api/options-control';
import * as queueApi from '../../api/queue-control';
import { QueueRegionId } from '../../model';
import { selectApplicationSetting, selectFavQueueFolderId, selectMyself } from '../reducers/config';
import { selectSelectedQueueView } from '../reducers/localSettings';
import { shouldFetchQueueMedia } from '../reducers/media';
import { shouldFetchExtensionDetails, shouldFetchQueueAgents } from '../reducers/queueAgents';
import { shouldFetchQueues } from '../reducers/queues';
import { wireUpApi } from '../tools/async-saga-types';
import { all } from 'async-saga';
import { takeEvery, waitForState } from '../tools/async-saga-types';
import { shouldFetchMyQueueEmails, shouldFetchQueueEmails } from '../reducers/queueEmails';

const fetchQueue = wireUpApi(actions.fetchQueue)
const fetchQueueAgents = wireUpApi(actions.fetchQueueAgents, shouldFetchQueueAgents)
const fetchQueueMedia = wireUpApi(actions.fetchQueueMedia, shouldFetchQueueMedia)
const fetchExtensionDetails = wireUpApi(actions.fetchExtensionDetails, shouldFetchExtensionDetails)
const fetchMyQueueEmails = wireUpApi(actions.fetchMyQueueEmails, shouldFetchMyQueueEmails)
const fetchQueueEmails = wireUpApi(actions.fetchQueueEmails, shouldFetchQueueEmails)

const fetchQueues = takeEvery(actions.fetchQueues.request, async (ctx, request) => {
  const state = ctx.getState()
  const virtualSetting = selectApplicationSetting(state, 'ShowVirtualQueues')

  if (!virtualSetting) return

  const virtual = virtualSetting.value === 'true'

  if ((request.refresh === undefined || !request.refresh) && !shouldFetchQueues(state, request.region, virtual)) { return }

  try {
    if (request.region === QueueRegionId.Squads) {
      ctx.dispatch(actions.fetchQueues.response({ region: request.region, squads: await api.getSquads() }))
    } else {
      ctx.dispatch(actions.fetchQueues.response({ region: request.region, queues: await queueApi.getQueues(request.region, virtual), virtual }))
    }
  } catch (ex) {
    api.processApiError(ex, ctx.dispatch)
  }
})

const fetchSquads = takeEvery(actions.fetchSquads, async (ctx) => {
  ctx.dispatch(actions.fetchQueues.response({ region: QueueRegionId.Squads, squads: await api.getSquads() }))
})

const fetchSquadAgents = takeEvery(actions.fetchSquadAgents, async (ctx, { queue }) => {
  ctx.dispatch(actions.fetchQueueAgents.response({ queue, agents: await api.getQueueAgents(queue) }))
})

const fetchSquadMedia = takeEvery(actions.fetchSquadMedia, async (ctx, { queue }) => {
  ctx.dispatch(actions.fetchQueueMedia.response({ queue, media: await api.getQueueMedia(queue) }))
})

const fetchFavQueues = takeEvery(actions.fetchFavQueues.request, async (ctx) => {
  const state = ctx.getState()
  const virtualSetting = selectApplicationSetting(state, 'ShowVirtualQueues')

  if (!virtualSetting) return

  const virtual = virtualSetting.value === 'true'

  try {
    const queues = await queueApi.getQueues(QueueRegionId.Favourites, virtual);
    ctx.dispatch(actions.fetchFavQueues.response(queues))

  } catch (ex) {
    api.processApiError(ex, ctx.dispatch)
  }
})

const fetchMyQueues = takeEvery(actions.fetchMyQueues.request, async (ctx) => {
  const state = ctx.getState()
  const virtualSetting = selectApplicationSetting(state, 'ShowVirtualQueues')

  if (!virtualSetting) return

  const virtual = virtualSetting.value === 'true'

  try {
    const queues = await queueApi.getQueues(QueueRegionId.Mine, virtual);
    ctx.dispatch(actions.fetchMyQueues.response(queues))

  } catch (ex) {
    api.processApiError(ex, ctx.dispatch)
  }
})

const favouriteQueue = takeEvery(actions.favouriteQueue, async (ctx, { queue, add }) => {
  const parentId = await waitForState(ctx, selectFavQueueFolderId)

  if (add) {
    await api.addFavouriteQueue(queue)
  } else {
    await api.removeFavouriteQueue(queue, parentId)
  }

  ctx.dispatch(actions.favouriteQueueAction(queue, add, parentId))
})

const setVirtualQueueView = takeEvery(actions.setVirtualQueueView, async (ctx, virtual) => {
  const view = selectSelectedQueueView(ctx.getState())
  const state = selectApplicationSetting(ctx.getState(), 'ShowVirtualQueues')
  const myId = selectMyself(ctx.getState())
  if (myId && state) {
    //save to the server
    const s = await saveApplicationSettings(myId, { ...state, value: virtual ? 'true' : 'false' })
    //update cache
    ctx.dispatch(actions.updateApplicationSetting(s))

    //refresh view
    if (view.region) {
      ctx.dispatch(actions.fetchQueues.request({ region: view.region }))
    }
  }
})

const setExtensionAutoAnswer = takeEvery(actions.setExtensionAutoAnswer, async (ctx, auto) => {
  const state = selectApplicationSetting(ctx.getState(), 'ExtensionAutoAnswer')
  const myId = selectMyself(ctx.getState())
  if (myId && state) {
    //save to the server
    const s = await saveApplicationSettings(myId, { ...state, value: auto ? 'true' : 'false' })
    //update cache
    ctx.dispatch(actions.updateApplicationSetting(s))
  }
})

const setAgentViewOnAnswer = takeEvery(actions.setAgentViewOnAnswer, async (ctx, auto) => {
  const state = selectApplicationSetting(ctx.getState(), 'AgentViewOnAnswer')
  const myId = selectMyself(ctx.getState())
  if (myId && state) {
    //save to the server
    const s = await saveApplicationSettings(myId, { ...state, value: auto ? 'true' : 'false' })
    //update cache
    ctx.dispatch(actions.updateApplicationSetting(s))
  }
})

const setWebRtcEnabled = takeEvery(actions.setWebRtcEnabled, async (ctx, setting) => {
  const state = selectApplicationSetting(ctx.getState(), 'WebRtcEnabled')
  const myId = selectMyself(ctx.getState())
  if (myId && state) {
    //save to the server
    const s = await saveApplicationSettings(myId, { ...state, value: (setting ? '1' : '0') })
    //update cache
    ctx.dispatch(actions.updateApplicationSetting(s))
  }
})

const fetchQueuesByIds = takeEvery(actions.fetchQueuesByIds, async (ctx, { ids }) => {
  const store = ctx.getState()
  const mySelf = selectMyself(store)
  if (mySelf) {
    const queues = await queueApi.getQueuesByIds(mySelf, ids)

    ctx.dispatch(actions.fetchQueuesByIdsResponse(queues))
  }
})

const fetchGosLimits = wireUpApi(actions.fetchGosLimits)

const updateGosLimits = takeEvery(actions.updateGosLimits, async (ctx, { queueId, type, limits: [green, yellow, red] }) => {
  queueApi.setGosGrpahLimits([{
    queueId,
    type,
    red,
    yellow,
    green,
  }])
})

export default all(
  fetchQueue,
  fetchQueues,
  fetchFavQueues,
  fetchMyQueues,
  fetchQueueAgents,
  fetchQueueMedia,
  fetchExtensionDetails,
  favouriteQueue,
  setVirtualQueueView,
  fetchMyQueueEmails,
  fetchQueueEmails,
  fetchQueuesByIds,
  setExtensionAutoAnswer,
  setWebRtcEnabled,
  fetchGosLimits,
  updateGosLimits,
  fetchSquads,
  fetchSquadAgents,
  fetchSquadMedia,
  setAgentViewOnAnswer,
)
